import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { HealthUnit } from "@/@types/esus/HealthUnit";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Account } from "@/@types/app/Account";
import { FederalUnit } from "@/@types/app/FederalUnit";
import { Option } from "@/@types/generics/Option";
import { Module } from "@/@types/app/Module";
import {
	getPermittedCities,
	getPermittedFederalUnits,
	getPermittedHealthUnits,
	getPermittedModules,
} from "@/services/app/userService";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";

import { FederalUnitsSelect } from "@/components/FederalUnitsSelect";
import { CitiesSelect } from "@/components/CitiesSelect";
import { UnitsSelect } from "@/components/UnitsSelect";
import { ModuleSelect } from "@/components/ModuleSelect";

import "./styles.scss";

export function FormUnitSelection() {
	const navigate = useNavigate();
	const { currentFederalUnit, currentAccount, getCurrentAccount, currentModule } =
		useCurrentAccount();
	const [modules, setModules] = useState<Module[]>([{ description: "Selecione..." } as Module]);
	const [federalUnits, setFederalUnits] = useState<FederalUnit[]>([
		{ abbreviation: "Todos os estados" } as FederalUnit,
	]);
	const [cities, setCities] = useState<Account[]>([
		{ ibgeCode: "", locale: "Selecione..." } as Account,
	]);
	const [healthUnits, setHealthUnits] = useState<HealthUnit[]>([
		{ cnes: "", name: "Selecione..." } as HealthUnit,
	]);
	const [selectedOptionModule, setSelectedOptionModule] = useState<Option<number | undefined>>({
		label: currentModule.description || "Selecione...",
		value: currentModule.id,
	});
	const [selectedOptionFederalUnit, setSelectedOptionFederalUnit] = useState<Option<string>>({
		label: currentFederalUnit.abbreviation || "Selecione...",
		value: currentFederalUnit.abbreviation,
	});
	const [selectedOptionCity, setSelectedOptionCity] = useState<Option<string>>({
		label: currentAccount.locale || "Selecione...",
		value: currentAccount.ibgeCode,
	});
	const [selectedOptionUnit, setSelectedOptionUnit] = useState<Option<string>>();

	const selectedModule = selectedOptionModule?.value
		? ModuleEnum[ModuleEnum[Number(selectedOptionModule?.value)] as keyof typeof ModuleEnum]
		: undefined;

	const [inputSelectUnitDisable, setInputSelectUnitDisable] = useState<boolean>(
		currentAccount.ibgeCode ? false : true
	);
	const [buttonDisabled, setButtonDisabled] = useState(true);

	async function fetchPermittedModules() {
		setModules(await getPermittedModules());
	}
	async function fetchPermittedFederalUnits() {
		setFederalUnits(await getPermittedFederalUnits(selectedModule));
	}
	async function fetchPermittedCities() {
		setCities(await getPermittedCities(selectedModule, selectedOptionFederalUnit?.value));
	}

	async function fetchPermittedHealthUnits() {
		setHealthUnits(
			await getPermittedHealthUnits(
				selectedModule,
				selectedOptionFederalUnit?.value,
				selectedOptionCity?.value
			)
		);
	}

	useEffect(() => {
		fetchPermittedModules();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedOptionModule) {
			fetchPermittedFederalUnits();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptionModule]);

	useEffect(() => {
		if (selectedOptionFederalUnit) {
			fetchPermittedCities();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptionFederalUnit]);

	useEffect(() => {
		if (selectedOptionCity) {
			fetchPermittedHealthUnits();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptionCity]); // Depende do selectedOptionCity

	async function handleLogin(module?: ModuleEnum, uf?: string, ibgeCode?: string, cnes?: string) {
		await getCurrentAccount(module, uf, ibgeCode, cnes);
		navigate("/");
	}

	return (
		<div
			id="body-layout"
			className="d-flex flex-column justify-content-between bg-white mx-3"
			style={{ height: "25rem" }}
		>
			<Row>
				<Col className="mb-2">
					<ModuleSelect
						id="module-select"
						defaultValue={selectedOptionModule}
						value={selectedOptionModule}
						modules={modules}
						onChange={(newValue) => {
							setSelectedOptionModule(newValue as Option<number | undefined>);
							setSelectedOptionFederalUnit({
								label: "Selecione...",
							} as Option<string>);
							setSelectedOptionCity({} as Option<string>);
							setSelectedOptionUnit({} as Option<string>);
						}}
					/>
				</Col>
			</Row>
			<Row>
				<Col sm={12} md={12} lg={3} xl={3} className="mb-2">
					<FederalUnitsSelect
						id="federal-unit-select"
						defaultValue={selectedOptionFederalUnit}
						value={selectedOptionFederalUnit}
						federalUnits={federalUnits}
						onChange={(newValue) => {
							setSelectedOptionFederalUnit(newValue as Option<string>);
							setSelectedOptionCity({
								label: "Selecione...",
							} as Option<string>);
							setSelectedOptionUnit({} as Option<string>);
						}}
					/>
				</Col>
				<Col sm={12} md={12} lg={9} xl={9} className="mb-2">
					<CitiesSelect
						id="cities-select"
						defaultValue={selectedOptionCity}
						value={selectedOptionCity}
						accounts={cities}
						onChange={(newValue) => {
							setSelectedOptionCity(newValue as Option<string>);
							setSelectedOptionUnit({
								label: "Selecione...",
							} as Option<string>);
							setInputSelectUnitDisable(false);
						}}
					/>
				</Col>
			</Row>
			<Row>
				<Col className="mb-2">
					<UnitsSelect
						id="units-select"
						noDefaultOption={true}
						units={healthUnits}
						value={selectedOptionUnit}
						isDisabled={inputSelectUnitDisable}
						onChange={(newValue) => {
							setSelectedOptionUnit(newValue as Option<string>);
							setButtonDisabled(false);
						}}
					/>
				</Col>
			</Row>
			<Row>
				<Col className="mb-2">
					<div className="position-relative text-center mt-5 w-auto p-2 mb-2">
						<Button
							disabled={buttonDisabled}
							className="button-entrar mb-2 "
							style={{ width: "20rem" }}
							onClick={() =>
								handleLogin(
									selectedModule,
									selectedOptionFederalUnit?.value,
									selectedOptionCity?.value,
									selectedOptionUnit?.value
								)
							}
						>
							Entrar
						</Button>
					</div>
				</Col>
			</Row>
		</div>
	);
}
